import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { getEvent } from '@/lib/events-api';
import { CheckCircle, MapPin } from 'lucide-react';

export default function EventJoinedPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const eventId = sp.get('event_id') || '';
  const { ready } = useRequireAuth();
  const [event, setEvent] = useState(null);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId || !ready) return;
    (async () => {
      try {
        const body = await getEvent(eventId);
        setEvent(body.event); setMembership(body.membership);
      } finally { setLoading(false); }
    })();
  }, [eventId, ready]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="event-joined-loading">
        <div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const canUpload = !!membership?.canUpload;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="event-joined-screen">
      <div className="w-full max-w-md flex flex-col gap-4">
        <div className="mb-1"><div className="flex items-center gap-2"><img src="/allviews-mark.svg" alt="" className="h-5 w-5" /><img src="/allviews-wordmark.svg" alt="ALLVIEWS" className="h-3.5" /></div></div>

        <div className="bg-[#E4F4EC] border border-[#1F8A55]/20 rounded-xl p-4" data-testid="joined-success-card">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-[#1F8A55] flex-shrink-0" />
            <div>
              <h1 className="text-xl font-extrabold text-[#0B1B46]" data-testid="joined-title">You're in.</h1>
              <p className="text-sm text-[#5B6472]">Welcome to {event?.name}.</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="joined-membership-card">
          <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest mb-2 uppercase">Membership status</p>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#E4F4EC] text-[#1F8A55]" data-testid="joined-membership-status">{membership?.status || 'REGISTERED'}</span>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${canUpload ? 'bg-[#E4F4EC] text-[#1F8A55]' : 'bg-[#FBEEDA] text-[#B86500]'}`} data-testid="joined-can-upload">{canUpload ? 'Can upload' : 'Uploads locked'}</span>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#EFF5FF] text-[#146BFF]" data-testid="joined-role">{membership?.role || 'CONTRIBUTOR'}</span>
          </div>
          <p className="text-sm text-[#5B6472]" data-testid="joined-privacy-message">Only you and the host can see your submissions before Lookbook launch.</p>
        </div>

        <div className="bg-[#EFF5FF] border border-[#D6E4FF] rounded-xl p-4" data-testid="joined-next-action-card">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-[#146BFF] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-[#0B1B46]" data-testid="joined-next-action-title">Next step: verify your attendance</p>
              <p className="text-sm text-[#5B6472] mt-1">We'll verify you're at the event location before enabling uploads.</p>
              <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#FBEEDA] text-[#B86500]" data-testid="joined-next-action-status">Verification required</span>
            </div>
          </div>
        </div>

        <button onClick={() => navigate(`/verify-attendance?event_id=${encodeURIComponent(eventId)}`, { replace: true })}
          className="w-full h-11 bg-[#146BFF] hover:bg-[#0F55CC] text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
          data-testid="joined-verify-btn">Verify My Attendance</button>
        <button onClick={() => navigate('/contrib-event', { replace: true })}
          className="w-full h-11 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
          data-testid="joined-home-btn">Go to Event Home</button>
        <button onClick={() => navigate('/', { replace: true })}
          className="w-full h-11 text-[#5B6472] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
          data-testid="joined-landing-btn">Back to Landing</button>
      </div>
    </div>
  );
}
